import {Component, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {App, AppId} from '../../../../shared/models/app';
import {TitleBreadcrumbsChangedAction} from '../../../../core/actions/title.actions';
import {Store} from '@ngrx/store';
import {AppState} from '../../../../app/app.state';
import {ActivatedRoute} from '@angular/router';
import {AppsService} from '../../../../core/services/apps.service';
import {AppsChangedId} from '../../../../core/actions/apps.actions';
import {Subscription} from 'rxjs';
import {AppsState} from '../../../../core/state/apps.state';
import {FileUploadService} from '../../../../core/services/file-upload.service';
import {Tile} from '../../../../shared/models/tile';
import {TilesService} from '../../../../core/services/tiles.service';
import {AppTemplateOccupantTenant} from '../../../../shared/models/app-template/tenant-portal/occupant';
import {TileMultiChoiceElemSetup, TileNumberElemSetup, TileToggleElemSetup} from '../../../../shared/models/tile-template/interfaces';
import {TileComponent} from '../../../components/tile/tile.component';

@Component({
  selector: 'motn-dashboard-tenant-portal',
  templateUrl: './dashboard-tenant-portal.component.html',
  styleUrls: ['./dashboard-tenant-portal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    TileComponent,
  ],
})
export class DashboardTenantPortalComponent implements OnInit, OnDestroy {
  app: App;
  appId: AppId;
  template: AppTemplateOccupantTenant;
  imgSource: string = '';
  tiles: Tile[];

  private routeSub: Subscription;
  private appsSub: Subscription;


  constructor(
    private store: Store<AppState>,
    private route: ActivatedRoute,
    private appsService: AppsService,
    private tilesService: TilesService,
    private fileUploadService: FileUploadService,
  ) {
  }


  ngOnInit(): void {
    this.routeSub = this.route.parent.paramMap.subscribe((params) => {
      this.appId = params.get('appId');
      this.store.dispatch(new AppsChangedId(this.appId));
      this.appsService.fetchApp(this.appId);
    });

    this.appsSub = this.store.select('apps').subscribe(
      (data: AppsState) => {
        this.app = data.apps.find((a) => {
          return a.ID === this.appId;
        });

        if (this.app) {
          this.updateTitle();

          this.template = this.app.Config.Template;

          if (this.app.Image) {
            this.fileUploadService.fetch(this.app.Image).subscribe(
              (data) => {
                if (data) {
                  this.imgSource = `url('${data.URL}')`;
                }
              },
            );
          }
        }
      },
    );

    this.tilesService.fetchAppTiles$(this.appId).subscribe(
      (tiles) => {
        this.tiles = tiles;
      },
    );
  }


  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
    this.appsSub?.unsubscribe();
  }


  setUpHvacTemperatureTile(zoneIndex: number): TileNumberElemSetup {
    const tileId = this.template.Modules.HVAC?.Zones[zoneIndex].Temperature?.TileID;

    return new TileNumberElemSetup({
      tile: this.tiles.find(t => t.ID === tileId),
      writable: false,
      displayName: 'Temperature',
      displayIcon: 'temperature-high',
      displayColor: 'indigo-white',
    });
  }


  setUpHvacHumidityTile(zoneIndex: number): TileNumberElemSetup {
    const tileId = this.template.Modules.HVAC?.Zones[zoneIndex].Humidity?.TileID;

    return new TileNumberElemSetup({
      tile: this.tiles.find(t => t.ID === tileId),
      writable: false,
      displayName: 'Humidity',
      displayIcon: 'droplet-degree',
      displayColor: 'indigo-white',
    });
  }


  setUpHvacSetPointTile(zoneIndex: number): TileNumberElemSetup {
    const tileId = this.template.Modules.HVAC?.Zones[zoneIndex].SetPoint?.TileID;

    return new TileNumberElemSetup({
      tile: this.tiles.find(t => t.ID === tileId),
      writable: true,
      displayName: 'Set Point',
      displayIcon: 'sort-circle',
      displayColor: 'indigo-white',
    });
  }


  setUpHvacSetPointCoolTile(zoneIndex: number): TileNumberElemSetup {
    const tileId = this.template.Modules.HVAC?.Zones[zoneIndex].SetPointCool?.TileID;

    return new TileNumberElemSetup({
      tile: this.tiles.find(t => t.ID === tileId),
      writable: true,
      displayName: 'Set Point (Cool)',
      displayIcon: 'snowflake',
      displayColor: 'indigo-white',
    });
  }


  setUpHvacSetPointHeatTile(zoneIndex: number): TileNumberElemSetup {
    const tileId = this.template.Modules.HVAC?.Zones[zoneIndex].SetPointHeat?.TileID;

    return new TileNumberElemSetup({
      tile: this.tiles.find(t => t.ID === tileId),
      writable: true,
      displayName: 'Set Point (Heat)',
      displayIcon: 'fire',
      displayColor: 'indigo-white',
    });
  }


  setUpHvacSystemModeTile(zoneIndex: number): TileMultiChoiceElemSetup {
    const tileId = this.template.Modules.HVAC?.Zones[zoneIndex].SystemMode?.TileID;

    return new TileMultiChoiceElemSetup({
      tile: this.tiles.find(t => t.ID === tileId),
      writable: false,
      displayName: 'System Mode',
      displayIcon: 'circle-half-stroke',
      displayColor: 'indigo-black',
    });
  }


  setUpHvacFanAlwaysOnTile(zoneIndex: number): TileToggleElemSetup {
    const tileId = this.template.Modules.HVAC?.Zones[zoneIndex].FanAlwaysOn?.TileID;

    return new TileToggleElemSetup({
      tile: this.tiles.find(t => t.ID === tileId),
      writable: false,
      displayName: 'Fan Always On',
      displayIcon: 'fan',
      displayColor: '',
      showToggle: true,
      showStateText: false,
    });
  }


  setUpHvacOnOffTile(zoneIndex: number): TileToggleElemSetup {
    const tileId = this.template.Modules.HVAC?.Zones[zoneIndex].OnOff?.TileID;

    return new TileToggleElemSetup({
      tile: this.tiles.find(t => t.ID === tileId),
      writable: true,
      displayName: 'HVAC On/Off',
      displayIcon: 'power-off',
      displayColor: 'indigo-black',
      showToggle: true,
      showStateText: false,
    });
  }


  private updateTitle() {
    this.store.dispatch(new TitleBreadcrumbsChangedAction(
      [{
        path: '',
        text: 'Tenant Portal',
      }],
    ));
  }
}
